import React, { ReactElement } from 'react';

import Script from 'next/script';

import type { FunnelClass } from '@onark/shared/welcomeAboardFunnelClass';
import {
  GA_CONSENT_DEFAULT_SNIPPET,
  GA_MEASUREMENT_ID,
} from '@OnArk/utils/ga4';
import { GPC_ACTIVE } from '@OnArk/utils/gpc';
import { META_PIXEL_HOST_GUARD, META_PIXEL_ID } from '@OnArk/utils/metaPixel';

// Sourced from .env / .env.production. NEXT_PUBLIC_* values get
// inlined at build time, so referencing process.env directly here is
// safe — no need for the deprecated publicRuntimeConfig pattern.
const ROLLBAR_CLIENT_TOKEN = process.env.NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN ?? '';

// LinkedIn Insight Tag partner id (OnArk ad account 547331060). Not a secret;
// it ships to the browser like the GA/Meta ids below, and it's baked in so no
// deploy config is needed (an env override is still honored). The tag only
// renders in production — see the `env === 'production'` gate below — so
// dev/staging traffic never pollutes the LinkedIn retargeting audiences. Keep
// this value in sync with the marketing site's head-scripts.tsx so a
// hi.onark.app → app.onark.app journey is a single LinkedIn visitor.
const LINKEDIN_PARTNER_ID =
  process.env.NEXT_PUBLIC_LINKEDIN_PARTNER_ID ?? '9341218';

/**
 * Which tracking scripts a page gets. `onarkFacing` is the admin app + the
 * PUBLIC sign-up/start pages; the Meta Pixel additionally reaches, of the
 * GUEST class pages, only OnArk's own funnel classes (see the prop below).
 * Never AUTH, never a customer church's class takers.
 */
function trackingScope(
  layout: string,
  funnelClass: FunnelClass | null | undefined
): { metaPixel: boolean; onarkFacing: boolean } {
  const onarkFacing = layout?.includes('ADMIN') || layout === 'PUBLIC';

  return {
    metaPixel: onarkFacing || (layout === 'GUEST' && !!funnelClass),
    onarkFacing,
  };
}

function HeadScripts({
  funnelClass,
  layout,
  user,
}: {
  user: WithAuthUser | null | undefined;
  layout: string;
  /**
   * Set by the guest check-in page when the class is one of OnArk's OWN
   * funnel classes (the free training / the Welcome Aboard master). Those
   * visitors are OnArk prospects who arrived off an ad — the check-in page
   * reports their check-in as a Meta lead, which needs the pixel on the page.
   * A customer church's guests are that church's people and never get it.
   */
  funnelClass?: FunnelClass | null;
}): ReactElement {
  // NODE_ENV is 'production' for staging's production-mode builds too, so the
  // Rollbar environment gets its own build-baked tag (CI derives it from the
  // branch; see frontend.yml build-args).
  const env =
    process.env.NEXT_PUBLIC_ROLLBAR_ENV ||
    process.env.NODE_ENV ||
    'development';
  const { metaPixel, onarkFacing } = trackingScope(layout, funnelClass);

  return (
    <>
      <Script
        dangerouslySetInnerHTML={{
          __html: `

              // captureUncaught grabs *every* uncaught error on the page,
              // including ones thrown by the visitor's browser extensions
              // (PayPal Honey, ad blockers, etc.) which are not our code and
              // are unactionable. Drop anything whose stack lives in an
              // extension bundle. indexOf (not regex) on purpose: this string
              // is a template literal, and regex backslashes would be eaten by
              // its escape processing before reaching the browser.
              function _rbFromExtension(s) {
                if (!s || typeof s !== 'string') return false;
                var f = s.toLowerCase();
                return f.indexOf('chrome-extension://') !== -1
                    || f.indexOf('moz-extension://') !== -1
                    || f.indexOf('safari-extension://') !== -1
                    || f.indexOf('safari-web-extension://') !== -1
                    || f.indexOf('.safariextension/') !== -1
                    || f.indexOf('.appex/') !== -1;
              }

              var _rollbarConfig = {
                enabled: ${env !== 'development'},
                accessToken: "${ROLLBAR_CLIENT_TOKEN}",
                captureUncaught: true,
                captureUnhandledRejections: true,
                checkIgnore: function (isUncaught, args, payload) {
                  try {
                    var body = (payload && (payload.body || (payload.data && payload.data.body))) || {};
                    var traces = [];
                    if (body.trace) traces.push(body.trace);
                    if (body.trace_chain && body.trace_chain.length) traces = traces.concat(body.trace_chain);
                    for (var i = 0; i < traces.length; i++) {
                      var frames = (traces[i] && traces[i].frames) || [];
                      for (var j = 0; j < frames.length; j++) {
                        if (frames[j] && _rbFromExtension(frames[j].filename)) return true;
                      }
                    }
                    if (args && args.length) {
                      for (var k = 0; k < args.length; k++) {
                        var a = args[k];
                        if (typeof a === 'string' && _rbFromExtension(a)) return true;
                        if (a && a.stack && _rbFromExtension(a.stack)) return true;
                      }
                    }
                  } catch (e) {}
                  return false;
                },
                payload: {
                  environment: "${env}",
                  person: ${JSON.stringify(user)},
                  client: {
                    javascript: {
                      source_map_enabled: true,
                      code_version: "${process.env.NEXT_BUILD_ID}",
                      guess_uncaught_frames: true
                    }
                  }
                }
              };

// Rollbar Snippet
!function(r){var e={};function o(n){if(e[n])return e[n].exports;var t=e[n]={i:n,l:!1,exports:{}};return r[n].call(t.exports,t,t.exports,o),t.l=!0,t.exports}o.m=r,o.c=e,o.d=function(r,e,n){o.o(r,e)||Object.defineProperty(r,e,{enumerable:!0,get:n})},o.r=function(r){"undefined"!=typeof Symbol&&Symbol.toStringTag&&Object.defineProperty(r,Symbol.toStringTag,{value:"Module"}),Object.defineProperty(r,"__esModule",{value:!0})},o.t=function(r,e){if(1&e&&(r=o(r)),8&e)return r;if(4&e&&"object"==typeof r&&r&&r.__esModule)return r;var n=Object.create(null);if(o.r(n),Object.defineProperty(n,"default",{enumerable:!0,value:r}),2&e&&"string"!=typeof r)for(var t in r)o.d(n,t,function(e){return r[e]}.bind(null,t));return n},o.n=function(r){var e=r&&r.__esModule?function(){return r.default}:function(){return r};return o.d(e,"a",e),e},o.o=function(r,e){return Object.prototype.hasOwnProperty.call(r,e)},o.p="",o(o.s=0)}([function(r,e,o){"use strict";var n=o(1),t=o(5);_rollbarConfig=_rollbarConfig||{},_rollbarConfig.rollbarJsUrl=_rollbarConfig.rollbarJsUrl||"https://cdn.rollbar.com/rollbarjs/refs/tags/v2.26.1/rollbar.min.js",_rollbarConfig.async=void 0===_rollbarConfig.async||_rollbarConfig.async;var a=n.setupShim(window,_rollbarConfig),l=t(_rollbarConfig);window.rollbar=n.Rollbar,a.loadFull(window,document,!_rollbarConfig.async,_rollbarConfig,l)},function(r,e,o){"use strict";var n=o(2),t=o(3);function a(r){return function(){try{return r.apply(this,arguments)}catch(r){try{console.error("[Rollbar]: Internal error",r)}catch(r){}}}}var l=0;function i(r,e){this.options=r,this._rollbarOldOnError=null;var o=l++;this.shimId=function(){return o},"undefined"!=typeof window&&window._rollbarShims&&(window._rollbarShims[o]={handler:e,messages:[]})}var s=o(4),d=function(r,e){return new i(r,e)},c=function(r){return new s(d,r)};function u(r){return a((function(){var e=this,o=Array.prototype.slice.call(arguments,0),n={shim:e,method:r,args:o,ts:new Date};window._rollbarShims[this.shimId()].messages.push(n)}))}i.prototype.loadFull=function(r,e,o,n,t){var l=!1,i=e.createElement("script"),s=e.getElementsByTagName("script")[0],d=s.parentNode;i.crossOrigin="",i.src=n.rollbarJsUrl,o||(i.async=!0),i.onload=i.onreadystatechange=a((function(){if(!(l||this.readyState&&"loaded"!==this.readyState&&"complete"!==this.readyState)){i.onload=i.onreadystatechange=null;try{d.removeChild(i)}catch(r){}l=!0,function(){var e;if(void 0===r._rollbarDidLoad){e=new Error("rollbar.js did not load");for(var o,n,a,l,i=0;o=r._rollbarShims[i++];)for(o=o.messages||[];n=o.shift();)for(a=n.args||[],i=0;i<a.length;++i)if("function"==typeof(l=a[i])){l(e);break}}"function"==typeof t&&t(e)}()}})),d.insertBefore(i,s)},i.prototype.wrap=function(r,e,o){try{var n;if(n="function"==typeof e?e:function(){return e||{}},"function"!=typeof r)return r;if(r._isWrap)return r;if(!r._rollbar_wrapped&&(r._rollbar_wrapped=function(){o&&"function"==typeof o&&o.apply(this,arguments);try{return r.apply(this,arguments)}catch(o){var e=o;throw e&&("string"==typeof e&&(e=new String(e)),e._rollbarContext=n()||{},e._rollbarContext._wrappedSource=r.toString(),window._rollbarWrappedError=e),e}},r._rollbar_wrapped._isWrap=!0,r.hasOwnProperty))for(var t in r)r.hasOwnProperty(t)&&(r._rollbar_wrapped[t]=r[t]);return r._rollbar_wrapped}catch(e){return r}};for(var p="log,debug,info,warn,warning,error,critical,global,configure,handleUncaughtException,handleAnonymousErrors,handleUnhandledRejection,captureEvent,captureDomContentLoaded,captureLoad".split(","),f=0;f<p.length;++f)i.prototype[p[f]]=u(p[f]);r.exports={setupShim:function(r,e){if(r){var o=e.globalAlias||"Rollbar";if("object"==typeof r[o])return r[o];r._rollbarShims={},r._rollbarWrappedError=null;var l=new c(e);return a((function(){e.captureUncaught&&(l._rollbarOldOnError=r.onerror,n.captureUncaughtExceptions(r,l,!0),e.wrapGlobalEventHandlers&&t(r,l,!0)),e.captureUnhandledRejections&&n.captureUnhandledRejections(r,l,!0);var a=e.autoInstrument;return!1!==e.enabled&&(void 0===a||!0===a||function(r){return!("object"!=typeof r||void 0!==r.page&&!r.page)}(a))&&r.addEventListener&&(r.addEventListener("load",l.captureLoad.bind(l)),r.addEventListener("DOMContentLoaded",l.captureDomContentLoaded.bind(l))),r[o]=l,l}))()}},Rollbar:c}},function(r,e,o){"use strict";function n(r,e,o,n){r._rollbarWrappedError&&(n[4]||(n[4]=r._rollbarWrappedError),n[5]||(n[5]=r._rollbarWrappedError._rollbarContext),r._rollbarWrappedError=null);var t=e.handleUncaughtException.apply(e,n);o&&o.apply(r,n),"anonymous"===t&&(e.anonymousErrorsPending+=1)}r.exports={captureUncaughtExceptions:function(r,e,o){if(r){var t;if("function"==typeof e._rollbarOldOnError)t=e._rollbarOldOnError;else if(r.onerror){for(t=r.onerror;t._rollbarOldOnError;)t=t._rollbarOldOnError;e._rollbarOldOnError=t}e.handleAnonymousErrors();var a=function(){var o=Array.prototype.slice.call(arguments,0);n(r,e,t,o)};o&&(a._rollbarOldOnError=t),r.onerror=a}},captureUnhandledRejections:function(r,e,o){if(r){"function"==typeof r._rollbarURH&&r._rollbarURH.belongsToShim&&r.removeEventListener("unhandledrejection",r._rollbarURH);var n=function(r){var o,n,t;try{o=r.reason}catch(r){o=void 0}try{n=r.promise}catch(r){n="[unhandledrejection] error getting \`promise\` from event"}try{t=r.detail,!o&&t&&(o=t.reason,n=t.promise)}catch(r){}o||(o="[unhandledrejection] error getting \`reason\` from event"),e&&e.handleUnhandledRejection&&e.handleUnhandledRejection(o,n)};n.belongsToShim=o,r._rollbarURH=n,r.addEventListener("unhandledrejection",n)}}}},function(r,e,o){"use strict";function n(r,e,o){if(e.hasOwnProperty&&e.hasOwnProperty("addEventListener")){for(var n=e.addEventListener;n._rollbarOldAdd&&n.belongsToShim;)n=n._rollbarOldAdd;var t=function(e,o,t){n.call(this,e,r.wrap(o),t)};t._rollbarOldAdd=n,t.belongsToShim=o,e.addEventListener=t;for(var a=e.removeEventListener;a._rollbarOldRemove&&a.belongsToShim;)a=a._rollbarOldRemove;var l=function(r,e,o){a.call(this,r,e&&e._rollbar_wrapped||e,o)};l._rollbarOldRemove=a,l.belongsToShim=o,e.removeEventListener=l}}r.exports=function(r,e,o){if(r){var t,a,l="EventTarget,Window,Node,ApplicationCache,AudioTrackList,ChannelMergerNode,CryptoOperation,EventSource,FileReader,HTMLUnknownElement,IDBDatabase,IDBRequest,IDBTransaction,KeyOperation,MediaController,MessagePort,ModalWindow,Notification,SVGElementInstance,Screen,TextTrack,TextTrackCue,TextTrackList,WebSocket,WebSocketWorker,Worker,XMLHttpRequest,XMLHttpRequestEventTarget,XMLHttpRequestUpload".split(",");for(t=0;t<l.length;++t)r[a=l[t]]&&r[a].prototype&&n(e,r[a].prototype,o)}}},function(r,e,o){"use strict";function n(r,e){this.impl=r(e,this),this.options=e,function(r){for(var e=function(r){return function(){var e=Array.prototype.slice.call(arguments,0);if(this.impl[r])return this.impl[r].apply(this.impl,e)}},o="log,debug,info,warn,warning,error,critical,global,configure,handleUncaughtException,handleAnonymousErrors,handleUnhandledRejection,_createItem,wrap,loadFull,shimId,captureEvent,captureDomContentLoaded,captureLoad".split(","),n=0;n<o.length;n++)r[o[n]]=e(o[n])}(n.prototype)}n.prototype._swapAndProcessMessages=function(r,e){var o,n,t;for(this.impl=r(this.options);o=e.shift();)n=o.method,t=o.args,this[n]&&"function"==typeof this[n]&&("captureDomContentLoaded"===n||"captureLoad"===n?this[n].apply(this,[t[0],o.ts]):this[n].apply(this,t));return this},r.exports=n},function(r,e,o){"use strict";r.exports=function(r){return function(e){if(!e&&!window._rollbarInitialized){for(var o,n,t=(r=r||{}).globalAlias||"Rollbar",a=window.rollbar,l=function(r){return new a(r)},i=0;o=window._rollbarShims[i++];)n||(n=o.handler),o.handler._swapAndProcessMessages(l,o.messages);window[t]=n,window._rollbarInitialized=!0}}}}]);
// End Rollbar Snippet

`,
        }}
        id="updates"
      />

      {(layout?.includes('ADMIN') || layout === 'AUTH') && (
        <>
          {/* Help Scout Beacon */}
          <Script
            dangerouslySetInnerHTML={{
              __html: `!function(e,t,n){function a(){var e=t.getElementsByTagName("script")[0],n=t.createElement("script");n.type="text/javascript",n.async=!0,n.src="https://beacon-v2.helpscout.net",e.parentNode.insertBefore(n,e)}if(e.Beacon=n=function(t,n,a){e.Beacon.readyQueue.push({method:t,options:n,data:a})},n.readyQueue=[],"complete"===t.readyState)return a();e.attachEvent?e.attachEvent("onload",a):e.addEventListener("load",a,!1)}(window,document,window.Beacon||function(){});window.Beacon('init','da166cf3-5feb-44d9-a2fe-757a9b8eefad');`,
            }}
            id="help-scout-beacon"
            strategy="afterInteractive"
          />
        </>
      )}

      {(layout?.includes('ADMIN') ||
        layout === 'PUBLIC' ||
        layout === 'AUTH') && (
        <>
          {/* GA4 — same measurement ID the old Squarespace marketing site used
              (hi.onark.app, now a 308 to onark.app — see next.config redirects)
              so journeys from marketing → sign-up are one continuous session.
              Scoped to OnArk-facing pages only (excludes GUEST class takers). */}
          <Script
            id="ga4-loader"
            src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
            strategy="afterInteractive"
          />
          <Script
            dangerouslySetInnerHTML={{
              __html: `
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  ${GA_CONSENT_DEFAULT_SNIPPET}
  gtag('config', '${GA_MEASUREMENT_ID}');`,
            }}
            id="ga4-init"
            strategy="afterInteractive"
          />
        </>
      )}

      {metaPixel && (
        <>
          {/* Meta Pixel — admin app + PUBLIC sign-up/start pages + OnArk's own
              funnel classes (never a customer church's class takers, never AUTH) */}
          <Script
            dangerouslySetInnerHTML={{
              __html: `
  if (${META_PIXEL_HOST_GUARD}) {
  !function(f,b,e,v,n,t,s)
  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
  n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];
  s.parentNode.insertBefore(t,s)}(window, document,'script',
  'https://connect.facebook.net/en_US/fbevents.js');
  fbq('init', '${META_PIXEL_ID}');
  fbq('track', 'PageView');
  }`,
            }}
            id="facebook-pixel"
            strategy="afterInteractive"
          />

          <noscript>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              alt="Facebook Pixel"
              height="1"
              src={`https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`}
              style={{ display: 'none' }}
              width="1"
            />
          </noscript>

          {/* LinkedIn Insight Tag — ADMIN + PUBLIC only (sign-up/checkout
              funnel, not GUEST class takers — not even the funnel classes,
              which only the Meta lead needs), and production only so
              dev/staging stay out of the retargeting audiences. */}
          {onarkFacing && env === 'production' && LINKEDIN_PARTNER_ID && (
            <>
              <Script
                dangerouslySetInnerHTML={{
                  __html: `
  if (!${GPC_ACTIVE}) {
  _linkedin_partner_id = "${LINKEDIN_PARTNER_ID}";
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  window._linkedin_data_partner_ids.push(_linkedin_partner_id);
  }`,
                }}
                id="linkedin-insight-init"
                strategy="afterInteractive"
              />
              <Script
                dangerouslySetInnerHTML={{
                  __html: `
  if (!${GPC_ACTIVE}) {
  (function(l) {
  if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
  window.lintrk.q=[]}
  var s = document.getElementsByTagName("script")[0];
  var b = document.createElement("script");
  b.type = "text/javascript";b.async = true;
  b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
  s.parentNode.insertBefore(b, s);})(window.lintrk);
  }`,
                }}
                id="linkedin-insight"
                strategy="afterInteractive"
              />
              <noscript>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  alt="LinkedIn Insight Tag"
                  height="1"
                  src={`https://px.ads.linkedin.com/collect/?pid=${LINKEDIN_PARTNER_ID}&fmt=gif`}
                  style={{ display: 'none' }}
                  width="1"
                />
              </noscript>
            </>
          )}
        </>
      )}
    </>
  );
}

export default HeadScripts;
